<template>
  <section class="policy-row">
    <!-- 회사 정보 입력칸과 따로 저장되는 설정이라 별도 상자로 둔다(H2). 루트 앞에 주석을 두면 $el 이 주석이 되어 .modal 을 못 찾는다 -->
    <div class="policy-caption">
      보안 설정
      <span class="policy-instant">바로 적용됩니다 · 「수정하기」와 별개</span>
    </div>
    <div class="policy-head">
      <span class="policy-label">회사 전체 2차 인증 필수</span>
      <span class="policy-state" :class="{ on: required }">{{
        loadFailed ? "확인 못 함" : required ? "켜짐" : "꺼짐"
      }}</span>
      <a-switch
        :checked="required"
        :loading="loading"
        :disabled="!companyId || !!loadFailed || !canSecurity"
        aria-label="회사 전체 2차 인증 필수"
        @change="change"
      />
    </div>
    <!-- 서버가 토큰 클레임이 아니라 회사 설정을 바로 보므로(D-118) 이미 로그인한 세션에도 곧 적용된다 -->
    <p class="policy-help">
      켜면 이미 로그인한 직원도 다음 저장·발송 때부터 2차 인증(수단이 없으면 등록)을 요구받습니다.
    </p>
    <div v-if="loadFailed" class="policy-error" role="alert">
      <span>{{ loadFailed }}</span>
      <a-button size="small" :loading="loading" @click="load">다시 불러오기</a-button>
    </div>
    <!-- 결과는 스위치 바로 아래에. 전역 알림은 이 부트스트랩 창 뒤에 가릴 수 있다(H1) -->
    <p v-else-if="result" class="policy-result" :class="result.type" role="status">
      {{ result.text }}
    </p>
  </section>
</template>

<script>
import apiInstance from "@/axios";
import { confirmDialog } from "@/utils/utils";
import { errorMessage, isStepUpCancelled } from "@/utils/mfa";
import { canNow } from "@/utils/perm";

/**
 * 운영자 회사 강제 스위치(design.md §6-3 C8, U-20·D-79·D-97). 병원·제조사(기공소)·STUDIO 회사 정보 창이 같이 쓴다.
 * 리셀러 회사에는 넣지 않는다(서버도 400).
 * PUT 은 늘 요구(M!) 경로라 증표가 없으면 axios 인터셉터가 본인 확인 창을 띄우고 다시 보낸다.
 * 회사 정보 창(부트스트랩 모달)이 열릴 때마다 다시 읽어 다른 운영자가 바꾼 값도 보인다.
 * 켜기·끄기는 확인 즉시 저장되고 창의 「수정하기」「취소」와 관계없다 — 결과는 스위치 아래 줄에 남긴다.
 */
export default {
  name: "CompanyTwoFactorPolicySwitch",
  props: {
    companyId: { type: String, default: "" },
  },
  data() {
    return { required: false, loading: false, loadFailed: "", result: null };
  },
  watch: {
    companyId: { immediate: true, handler: "load" },
  },
  mounted() {
    this.modalEl = this.$el.closest && this.$el.closest(".modal");
    if (this.modalEl) this.modalEl.addEventListener("show.bs.modal", this.load);
  },
  beforeUnmount() {
    if (this.modalEl) this.modalEl.removeEventListener("show.bs.modal", this.load);
  },
  computed: {
    // 서버가 ops.security.manage 지정자만 받음(@PermStrict) — 없으면 바꿀 수 없게(10-30)
    canSecurity() {
      return canNow(this.$store.state.introspect, "ops.security.manage");
    },
  },
  methods: {
    path(companyId) {
      return `/admin/security/companies/${companyId}/two-factor-policy`;
    },
    async load() {
      const companyId = this.companyId;
      this.required = false;
      this.loadFailed = "";
      this.result = null;
      this.loading = !!companyId;
      if (!companyId) return;
      try {
        // 실패 문구는 상자 안 줄에만(전역 알림과 두 번 나오지 않게)
        const { data } = await apiInstance.get(this.path(companyId), {
          showGlobalLoading: false,
          showErrorNotice: false,
        });
        // 창을 연 사이 다른 회사로 바뀌었으면 늦게 온 응답은 버린다
        if (companyId === this.companyId) this.required = !!(data && data.required);
      } catch (error) {
        console.error(error);
        if (companyId === this.companyId) {
          this.loadFailed = errorMessage(error, "회사 2차 인증 설정을 불러오지 못했습니다.");
        }
      } finally {
        if (companyId === this.companyId) this.loading = false;
      }
    },
    async change(next) {
      const ok = await confirmDialog(
        next ? "회사 전체 2차 인증 필수 켜기" : "회사 전체 2차 인증 필수 끄기",
        next
          ? "켜면 이미 로그인한 직원도 다음 저장·발송 때부터 2차 인증(수단이 없으면 등록)을 요구받습니다. 「수정하기」를 누르지 않아도 바로 적용됩니다."
          : "끄면 본인이 수단을 등록한 직원만 2차 인증을 합니다. 「수정하기」를 누르지 않아도 바로 적용됩니다.",
        { confirmText: next ? "켜기" : "끄기" }
      );
      if (!ok) return;
      this.loading = true;
      this.result = null;
      try {
        const { data } = await apiInstance.put(
          this.path(this.companyId),
          { required: next },
          { showErrorNotice: false }
        );
        this.required = data && typeof data.required === "boolean" ? data.required : next;
        this.result = {
          type: "success",
          text: this.required
            ? "켰습니다. 이 회사 직원은 다음 저장·발송 때부터 2차 인증을 합니다."
            : "껐습니다. 본인이 수단을 등록한 직원만 2차 인증을 합니다.",
        };
      } catch (error) {
        console.error(error);
        // 실패하면 스위치는 원래 값 그대로(required 를 바꾸지 않음). 본인 확인을 스스로 취소했으면 알리지 않는다(D5)
        if (!isStepUpCancelled(error)) {
          this.result = {
            type: "error",
            text: `바꾸지 못했습니다. ${errorMessage(error, "잠시 뒤 다시 시도하세요.")}`,
          };
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* 「2차 인증」 창 요약 띠와 같은 회색 상자. 회사 정보 입력칸과 구분된다 */
.policy-row {
  margin: 4px 0 12px;
  padding: 14px 20px;
  border: 1px solid #e8ebed;
  border-radius: 12px;
  background: #f7f8f9;
  text-align: left;
}
.policy-caption {
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #4b5158;
}
.policy-instant {
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 10px;
  background: #fff4cc;
  font-weight: 600;
  color: #7a5a00;
}
.policy-head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.policy-label {
  margin-right: auto;
  font-size: 15px;
  font-weight: 700;
  color: #17191c;
}
.policy-state {
  font-size: 13px;
  font-weight: 600;
  color: #4b5158;
}
.policy-state.on {
  color: #0958d9;
}
/* 꺼짐 스위치가 앱 공통 연회색(#D1D6DB)이라 바탕과 구분이 안 된다(M15) */
.policy-head :deep(.ant-switch:not(.ant-switch-checked)) {
  background: #8d939a;
}
.policy-help {
  margin: 6px 0 0;
  font-size: 13px;
  color: #4b5158;
  line-height: 1.5;
}
.policy-error,
.policy-result {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.5;
}
.policy-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
/* 회색 바탕에서도 대비 4.5:1 이상(D8) */
.policy-error,
.policy-result.error {
  color: #b23a3a;
}
.policy-result.success {
  color: #237804;
}
</style>
